import {Injectable} from '@angular/core';
import {ModalController} from '@ionic/angular';
import {Observable, Subject} from 'rxjs';
import {ThermomixService} from './thermomix.service';
import {ThermomixMatchService} from './thermomix-match.service';
import {ThermomixStorageService} from '../storage/thermomix-storage.service';
import {CartService} from './cart.service';
import {ToastService} from '../core/toast.service';
import {ProductService} from './product.service';
import {AuthService} from '../global/auth.service';
import {UserService} from './user.service';
import {
  ThermomixMatchModalPage,
  ThermomixMatchModalResult
} from '../../modals/thermomix-match-modal/thermomix-match-modal.page';
import {Product} from '../../types/models';
import {
  ThermomixItem,
  ThermomixMatch,
  ThermomixMatchSource,
  ThermomixSyncItem,
  ThermomixSyncResult
} from '../../types/thermomix';

/** Zustand eines laufenden Syncs, damit der Button den Fortschritt zeigen kann. */
export interface ThermomixSyncProgress {
  Running: boolean;
  /** Bereits abgearbeitete Eintraege */
  Done: number;
  Total: number;
}

/**
 * Fuehrt den Sync der Thermomix-Einkaufsliste in den kiwi-Warenkorb durch:
 * Liste laden, jeden Eintrag einem Produkt zuordnen, bei unklaren Faellen den Kunden fragen
 * und die Treffer in den Warenkorb legen.
 */
@Injectable({
  providedIn: 'root'
})
export class ThermomixSyncService {

  private progressSubject: Subject<ThermomixSyncProgress> = new Subject<ThermomixSyncProgress>();
  private running = false;

  constructor(
    private thermomixService: ThermomixService,
    private matchService: ThermomixMatchService,
    private storageService: ThermomixStorageService,
    private cartService: CartService,
    private productService: ProductService,
    private authService: AuthService,
    private userService: UserService,
    private modalController: ModalController,
    private toastService: ToastService
  ) { }

  /**
   * Beim Abmelden werden die Thermomix-Daten vom Geraet entfernt: sie gehoeren zum Kunden,
   * nicht zum Geraet, und duerfen dem naechsten Nutzer nicht in die Haende fallen.
   */
  init() {
    this.authService.Logout.subscribe(async () => this.storageService.clear());
  }

  public get Progress(): Observable<ThermomixSyncProgress> {
    return this.progressSubject.asObservable();
  }

  isRunning(): boolean {
    return this.running;
  }

  /**
   * true, wenn der Sync-Button angezeigt werden soll: Plattform unterstuetzt den Login, der
   * Kunde hat Zugangsdaten hinterlegt, und ein Admin hat das Feature fuer ihn freigeschaltet.
   */
  async isAvailable(): Promise<boolean> {
    if (!this.thermomixService.isSupported() || !(await this.userService.isThermomixEnabled())) {
      return false;
    }
    return this.storageService.hasCredentials();
  }

  /**
   * Startet den Sync. Gibt das Ergebnis zurueck oder null, wenn der Sync nicht starten konnte;
   * Fehler werden dem Kunden als Hinweis angezeigt.
   */
  async synchronize(): Promise<ThermomixSyncResult> {
    if (this.running) {
      return null;
    }
    this.running = true;
    this.emitProgress(0, 0);
    try {
      const items = await this.thermomixService.getShoppingList();
      if (items.length === 0) {
        await this.toastService.presentInfo('Die Thermomix-Einkaufsliste ist leer.');
        return { Items: [], Added: 0, Skipped: 0 };
      }
      const result = await this.resolveItems(items);
      if (!result.Cancelled) {
        await this.addToCart(result);
        await this.presentSummary(result);
      }
      return result;
    } catch (e) {
      await this.toastService.presentError(e?.message || 'Die Synchronisation ist fehlgeschlagen.');
      return null;
    } finally {
      this.running = false;
      this.emitProgress(0, 0);
    }
  }

  /** Ordnet jedem Eintrag ein Produkt zu, notfalls durch Rueckfrage beim Kunden. */
  private async resolveItems(items: ThermomixItem[]): Promise<ThermomixSyncResult> {
    const syncItems: ThermomixSyncItem[] = [];
    for (let index = 0; index < items.length; index++) {
      this.emitProgress(index, items.length);
      const syncItem = await this.resolveItem(items[index], index + 1, items.length);
      if (!syncItem) {
        return this.toResult(syncItems, true);
      }
      syncItems.push(syncItem);
    }
    this.emitProgress(items.length, items.length);
    return this.toResult(syncItems, false);
  }

  /** Gibt null zurueck, wenn der Kunde den gesamten Sync abgebrochen hat. */
  private async resolveItem(item: ThermomixItem, position: number, total: number): Promise<ThermomixSyncItem> {
    const stored = await this.getStoredDecision(item);
    if (stored) {
      return stored;
    }
    const matches = await this.matchService.findMatches(item);
    const best = matches[0];
    if (best && best.Score >= ThermomixMatchService.MIN_AUTO_SCORE && this.productService.getProductInStock(best.Product)) {
      return this.toSyncItem(item, matches, best.Product, 'auto', best.Score);
    }
    return this.askCustomer(item, matches, position, total);
  }

  /**
   * Wendet eine frueher getroffene Entscheidung des Kunden an. Das gemerkte Produkt wird neu
   * geladen, damit Preis und Verfuegbarkeit aktuell sind; ist es nicht mehr verfuegbar, wird
   * wieder gefragt.
   */
  private async getStoredDecision(item: ThermomixItem): Promise<ThermomixSyncItem> {
    const decision = await this.storageService.getDecision(this.matchService.getDecisionKey(item));
    if (!decision) {
      return null;
    }
    if (decision.Ignored) {
      return this.toSyncItem(item, [], null, 'skipped');
    }
    if (!decision.ProductId) {
      return null;
    }
    const product = await this.productService.get(decision.ProductId);
    if (!product || !this.productService.getProductInStock(product)) {
      return null;
    }
    return this.toSyncItem(item, [], product, 'stored');
  }

  private async askCustomer(item: ThermomixItem, matches: ThermomixMatch[], position: number, total: number): Promise<ThermomixSyncItem> {
    const modal = await this.modalController.create({
      component: ThermomixMatchModalPage,
      componentProps: {
        Item: item,
        Matches: matches,
        Index: position,
        Total: total
      },
      cssClass: 'height-90 space-around'
    });
    await modal.present();
    const response = await modal.onDidDismiss();
    const result: ThermomixMatchModalResult = response?.data;
    // Ein Schliessen ohne Ergebnis (z.B. Wischen) zaehlt wie ein Abbruch des Syncs.
    if (!result || result.Cancel) {
      return null;
    }
    const key = this.matchService.getDecisionKey(item);
    if (result.Skip) {
      if (result.Remember) {
        await this.storageService.saveDecision({ Key: key, Name: item.Name, Ignored: true });
      }
      return this.toSyncItem(item, matches, null, 'skipped');
    }
    if (result.Remember && result.Product) {
      await this.storageService.saveDecision({
        Key: key,
        Name: item.Name,
        ProductId: result.Product.Id,
        ProductName: result.Product.NameShort || result.Product.Name
      });
    }
    return this.toSyncItem(item, matches, result.Product, 'customer');
  }

  private toSyncItem(item: ThermomixItem, matches: ThermomixMatch[], product: Product,
                     source: ThermomixMatchSource, score?: number): ThermomixSyncItem {
    return {
      Item: item,
      Matches: matches || [],
      Product: product,
      Amount: product ? this.matchService.getCartAmount(item, product) : 0,
      Source: source,
      Score: score
    };
  }

  private toResult(items: ThermomixSyncItem[], cancelled: boolean): ThermomixSyncResult {
    return {
      Items: items,
      Added: items.filter(syncItem => !!syncItem.Product).length,
      Skipped: items.filter(syncItem => !syncItem.Product).length,
      Cancelled: cancelled
    };
  }

  /**
   * Legt die zugeordneten Produkte in den Warenkorb. Pro Produkt wird die hoehere der beiden
   * Mengen genommen, damit ein zweiter Sync nichts verdoppelt und nichts entfernt, was der
   * Kunde selbst hinzugefuegt hat.
   */
  private async addToCart(result: ThermomixSyncResult) {
    const amounts: { [productId: string]: number } = {};
    for (const syncItem of result.Items) {
      if (!syncItem.Product?.Id || !syncItem.Amount) {
        continue;
      }
      amounts[syncItem.Product.Id] = (amounts[syncItem.Product.Id] || 0) + syncItem.Amount;
    }
    for (const productId of Object.keys(amounts)) {
      const existing = await this.cartService.getProduct(productId);
      const amount = Math.max(existing?.Amount || 0, amounts[productId]);
      await this.cartService.saveProduct({ ProductId: productId, Amount: amount });
    }
    await this.cartService.synchronizeCart(false);
  }

  private async presentSummary(result: ThermomixSyncResult) {
    if (result.Added === 0) {
      return this.toastService.presentWarning('Es wurde kein Produkt in den Warenkorb gelegt.');
    }
    const products = result.Added === 1 ? '1 Produkt' : `${result.Added} Produkte`;
    const message = result.Skipped > 0
      ? `${products} in den Warenkorb gelegt, ${result.Skipped} übersprungen.`
      : `${products} in den Warenkorb gelegt.`;
    return this.toastService.presentSuccess(message);
  }

  private emitProgress(done: number, total: number) {
    this.progressSubject.next({ Running: this.running, Done: done, Total: total });
  }
}
